import React, { memo } from "react";
import { FiMapPin } from "react-icons/fi";
import { FaMars, FaVenus, FaTransgender } from "react-icons/fa";
import { BsPatchCheckFill } from "react-icons/bs";
import { useLanguage } from "../../i18n";
import { getProfileLocationLabel } from "../../utils/profileLocation";
import { getProfileImageUrl, IMAGE_SIZES } from "../../utils/imageUtils";
import "./ProfileMapCard.css";

const GENDER_PRESENTATION = {
  Female: { kind: "female", label: "Female" },
  Male: { kind: "male", label: "Male" },
  Trans: { kind: "trans", label: "Trans" },
};

const GENDER_LOOKUP_ORDER = ["Female", "Male", "Trans"];

function getDisplayName(profile) {
  if (profile.displayName) return profile.displayName;
  if (profile.name) {
    return String(profile.name).replace(/,\s*\d+$/, "").trim();
  }
  const title = profile.title ?? "";
  return title.replace(/,\s*\d+$/, "").trim() || title;
}

function getDisplayAge(profile) {
  if (profile.age != null && profile.age !== "") return profile.age;
  const source = profile.title ?? profile.name ?? "";
  const match = String(source).match(/,\s*(\d{1,2})\s*$/);
  return match ? Number(match[1]) : null;
}

function getGenderPresentation(profile) {
  const types = profile.profileTypes || [];
  for (const type of GENDER_LOOKUP_ORDER) {
    if (types.includes(type)) {
      return GENDER_PRESENTATION[type];
    }
  }
  if (profile.gender && GENDER_PRESENTATION[profile.gender]) {
    return GENDER_PRESENTATION[profile.gender];
  }
  return null;
}

function isProfileVerified(profile) {
  if (profile.verified === true) return true;
  if (profile.services?.includes("Verified")) return true;
  return false;
}

function GenderIcon({ kind }) {
  if (kind === "female") {
    return <FaVenus className="profile-map-card__gender profile-map-card__gender--female" aria-hidden />;
  }
  if (kind === "male") {
    return <FaMars className="profile-map-card__gender profile-map-card__gender--male" aria-hidden />;
  }
  if (kind === "trans") {
    return <FaTransgender className="profile-map-card__gender profile-map-card__gender--trans" aria-hidden />;
  }
  if (kind === "couple") {
    return (
      <span className="profile-map-card__gender-pair" aria-hidden>
        <FaVenus className="profile-map-card__gender profile-map-card__gender--female" />
        <FaMars className="profile-map-card__gender profile-map-card__gender--male" />
      </span>
    );
  }
  return null;
}

function ProfileMapCard({ profile, selected = false, onClick }) {
  const { t } = useLanguage();
  const image = getProfileImageUrl(profile);
  const displayName = getDisplayName(profile);
  const age = getDisplayAge(profile);
  const location = getProfileLocationLabel(profile);
  const bio = profile.desc ?? profile.bio ?? profile.description ?? "";
  const gender = getGenderPresentation(profile);
  const verified = isProfileVerified(profile);

  const handleClick = () => {
    onClick?.(profile);
  };

  return (
    <button
      type="button"
      className={`profile-map-card${selected ? " profile-map-card--selected" : ""}${verified ? " profile-map-card--has-badge" : ""}`}
      onClick={handleClick}
    >
      <div className="profile-map-card__media">
        {image ? (
          <img
            src={image}
            alt=""
            className="profile-map-card__image"
            width={IMAGE_SIZES.carouselCard}
            height={IMAGE_SIZES.carouselCard}
            loading={selected ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={selected ? "high" : "auto"}
          />
        ) : (
          <span className="profile-map-card__fallback" aria-hidden />
        )}

        <div className="profile-map-card__overlay">
          {/* Reserved: favorite button */}
        </div>
      </div>

      <div className="profile-map-card__body">
        <div className="profile-map-card__title-row">
          <p className="profile-map-card__name">{displayName}</p>
          {gender ? (
            <span
              className="profile-map-card__gender-wrap"
              aria-label={gender.label}
              title={gender.label}
            >
              <GenderIcon kind={gender.kind} />
            </span>
          ) : null}
        </div>

        {age != null ? (
          <p className="profile-map-card__age">{age} {t("profile.years")}</p>
        ) : null}

        {location ? (
          <p className="profile-map-card__location">
            <FiMapPin className="profile-map-card__location-icon" aria-hidden />
            <span>{location}</span>
          </p>
        ) : null}

        {bio ? (
          <p className="profile-map-card__bio">{bio}</p>
        ) : null}

        <div className="profile-map-card__extras" aria-hidden>
          {/* Reserved: online status, distance, premium */}
        </div>
      </div>

      {verified ? (
        <span className="profile-map-card__badge profile-map-card__badge--verified">
          <BsPatchCheckFill className="profile-map-card__badge-icon" aria-hidden />
          <span className="profile-map-card__badge-label">{t("profile.verified")}</span>
        </span>
      ) : null}
    </button>
  );
}

export default memo(ProfileMapCard);
